/**
 * CommercialDashboard — visão pessoal do Comercial.
 *
 * Tudo é filtrado por ``created_by = current_user`` no backend. Responde a
 * "Como vai o meu funil? O que tenho de fazer hoje?".
 *
 * Layout:
 *   ▸ 4 KPIs: minhas RFQs activas, meu pipeline (€), minha taxa de fecho,
 *     follow-ups urgentes (>14d sem actividade).
 *   ▸ 3 charts: estado das minhas RFQs (donut), funil temporal (linhas),
 *     motivos das minhas perdas (donut).
 *   ▸ Tabela "Acções pendentes" — RFQs onde sou o próximo a agir
 *     (in_preparation / awaiting_commercial_margins / approved /
 *     in_negotiation). Reposta como pediste depois do refactor inicial.
 *   ▸ 2 tabelas: follow-ups urgentes + deadlines próximos.
 */
import { Link } from 'react-router-dom';
import {
  KpiGrid, DonutChartCard, TimeSeriesCard, OperationalTable, StatusBadge,
  HorizontalBarCard,
} from '@/components/dashboard';
import { formatDate } from '@/lib/utils';
import { t } from '@/i18n/translations';

export default function CommercialDashboard({ data, lang }) {
  const charts = data.charts || {};
  const tables = data.tables || {};

  return (
    <div className="space-y-6">
      <KpiGrid kpis={data.kpis || []} lang={lang} />

      <div className="grid gap-4 lg:grid-cols-3">
        <DonutChartCard
          title={t('chart_my_status', lang)}
          data={charts.own_status || []}
          lang={lang}
        />
        <TimeSeriesCard
          title={t('chart_my_funnel_timeseries', lang)}
          data={charts.funnel_timeseries || []}
          series={[
            { key: 'created', labelKey: 'series_created', color: '#3B82F6' },
            { key: 'won',     labelKey: 'series_won',     color: '#10B981' },
            { key: 'lost',    labelKey: 'series_lost',    color: '#EF4444' },
          ]}
          kind="line"
          lang={lang}
        />
        <DonutChartCard
          title={t('chart_my_loss_reasons', lang)}
          data={(charts.loss_reasons || []).map((d) => ({
            key: `loss_reason_${d.key}`, count: d.count,
          }))}
          lang={lang}
          valueLabel="lost_total"
        />
      </div>

      {/* "O que vendi historicamente?" — máquinas e operações de acabamento
          que aparecem nas RFQs ganhas DESTE comercial. Útil para identificar
          padrão pessoal e sugerir composição em propostas futuras. */}
      <div className="grid gap-4 lg:grid-cols-2">
        <HorizontalBarCard
          title={t('chart_my_machines_won', lang)}
          data={charts.machines_won || []}
          lang={lang}
          emptyKey="no_won_data"
        />
        <HorizontalBarCard
          title={t('chart_my_finishing_won', lang)}
          data={charts.finishing_won || []}
          lang={lang}
          showPercent
          emptyKey="no_won_data"
        />
      </div>

      <OperationalTable
        title={t('table_pending_action', lang)}
        items={tables.pending_action || []}
        lang={lang}
        emptyKey="no_pending_action"
        columns={[
          { key: 'reference_number', labelKey: 'rfq_ref',
            render: (r) => (
              <Link to={`/rfqs/${r.id}`} className="font-medium hover:underline">
                {r.reference_number}
              </Link>
            ),
          },
          { key: 'part_designation', labelKey: 'part_designation',
            render: (r) => (
              <span className="text-muted-foreground">{r.part_designation}</span>
            ),
          },
          { key: 'status', labelKey: 'status',
            render: (r) => <StatusBadge status={r.status} lang={lang} />,
          },
          { key: 'deadline', labelKey: 'deadline',
            render: (r) => (
              <span className="tnum text-muted-foreground">
                {formatDate(r.deadline, lang)}
              </span>
            ),
          },
          { key: 'days_in_state', labelKey: 'days_in_state', align: 'right',
            render: (r) => (
              <span className="tnum">
                {r.days_in_state == null ? '—' : `${r.days_in_state} d`}
              </span>
            ),
          },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <OperationalTable
          title={t('table_my_followup', lang)}
          items={tables.follow_up || []}
          lang={lang}
          emptyKey="no_followup"
          columns={[
            { key: 'reference_number', labelKey: 'rfq_ref',
              render: (r) => (
                <Link to={`/rfqs/${r.id}`} className="font-medium hover:underline">
                  {r.reference_number}
                </Link>
              ),
            },
            { key: 'part_designation', labelKey: 'part_designation',
              render: (r) => (
                <span className="text-muted-foreground">{r.part_designation}</span>
              ),
            },
            { key: 'status', labelKey: 'status',
              render: (r) => <StatusBadge status={r.status} lang={lang} />,
            },
            { key: 'days_idle', labelKey: 'days_idle', align: 'right',
              render: (r) => (
                <span className="tnum">
                  {r.days_idle == null ? '—' : `${r.days_idle} d`}
                </span>
              ),
            },
          ]}
        />
        <OperationalTable
          title={t('table_my_deadlines', lang)}
          items={tables.my_deadlines || []}
          lang={lang}
          emptyKey="no_near_deadline"
          columns={[
            { key: 'reference_number', labelKey: 'rfq_ref',
              render: (r) => (
                <Link to={`/rfqs/${r.id}`} className="font-medium hover:underline">
                  {r.reference_number}
                </Link>
              ),
            },
            { key: 'part_designation', labelKey: 'part_designation',
              render: (r) => (
                <span className="text-muted-foreground">{r.part_designation}</span>
              ),
            },
            { key: 'deadline', labelKey: 'deadline', align: 'right',
              render: (r) => (
                <span className="tnum text-warning">
                  {formatDate(r.deadline, lang)}
                </span>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
